import { ArrowLeft, Building2, Calendar, ChevronLeft, ChevronRight, ExternalLink, Globe, Layers, Linkedin, Mail, MapPin, Newspaper, Phone, ShieldCheck, Star, Store } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
    getFranchise,
    getFranchiseEstablishments,
    FranchiseEstablishmentPage,
    FranchiseEstablishmentView,
    FranchiseOwnerFilter,
    getFounderEstablishmentCounts,
    FranchiseDetail,
    FranchiseEstablishment,
    FranchiseHygieneResult,
    FranchiseHygieneSignal,
    FranchiseHygieneSort,
    FranchiseNewsItem,
} from "../../interfaces/establishments";
import { FounderCounts } from "../../lib/founderCounts";
import { EstablishmentLogo } from "../../components/franchise/EstablishmentLogo";
import { FranchiseLogo, BrandStack } from "../../components/franchise/FranchiseLogo";
import { NewsCategoryTag } from "../../components/franchise/NewsCategoryTag";
import { franchiseIdOf, useFranchiseLogoMap } from "../../lib/franchiseLogos";
import {
    AssociatedBrands,
    DepartmentBars,
    IntelKpis,
    IntelPanel,
    OpeningsChart,
} from "../../components/franchise/NetworkIntel";

/* ─────────────────────────── helpers ─────────────────────────── */

function formatCity(city: string): string {
    return city.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

function formatName(name: string): string {
    return name.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

function timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const h = Math.floor(diff / 3_600_000);
    if (h < 1) return "À l'instant";
    if (h < 24) return `Il y a ${h}h`;
    const d = Math.floor(h / 24);
    if (d === 1) return "Hier";
    if (d < 7) return `Il y a ${d}j`;
    return new Date(dateStr).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

function formatDate(dateString?: string | null): string {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

function getPersonInfo(est: FranchiseEstablishment) {
    const person = est.peoples?.[0];
    if (!person) return { id: null, name: null, phone: null, email: null, linkedin: null };
    const enrichments: any[] = person.enrichments || [];
    const find = (type: string) =>
        enrichments.find((e: any) => e.type === type && e.status === "approved");
    return {
        id: person._id || null,
        name: person.name || null,
        phone: est.phone || find("phone")?.data?.phone || null,
        email: est.email || find("email_company")?.data?.email || null,
        linkedin: find("linkedin")?.data?.url || null,
    };
}

function managerCountLabel(counts?: FounderCounts): string | null {
    if (!counts) return null;
    const { active, closed } = counts;
    return `${active} actif${active > 1 ? "s" : ""} · ${closed} inactif${closed > 1 ? "s" : ""}`;
}

/* ─────────────────────────── Establishments table ─────────────────────────── */

function EstablishmentsTable({ establishments }: { establishments: FranchiseEstablishment[] }) {
    const navigate = useNavigate();
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [managerCounts, setManagerCounts] = useState<Record<string, FounderCounts>>({});
    const [hoveredPhone, setHoveredPhone] = useState<string | null>(null);
    const [hoveredEmail, setHoveredEmail] = useState<string | null>(null);
    const [hoveredLinkedin, setHoveredLinkedin] = useState<string | null>(null);

    const phoneRef = useRef<HTMLDivElement>(null);
    const emailRef = useRef<HTMLDivElement>(null);
    const linkedinRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleOutside = (e: MouseEvent) => {
            if (phoneRef.current && !phoneRef.current.contains(e.target as Node)) setHoveredPhone(null);
            if (emailRef.current && !emailRef.current.contains(e.target as Node)) setHoveredEmail(null);
            if (linkedinRef.current && !linkedinRef.current.contains(e.target as Node)) setHoveredLinkedin(null);
        };
        document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, []);

    const brands = useFranchiseLogoMap();

    // Active / inactive establishment counts per gérant, one batched call for the whole table
    // (same endpoint the signal and establishment views use).
    useEffect(() => {
        const ids = [...new Set(
            establishments.flatMap((e) => (e.peoples ?? []).map((p) => p._id)).filter(Boolean) as string[]
        )];
        if (ids.length === 0) return;
        getFounderEstablishmentCounts(ids).then(setManagerCounts).catch(() => { /* ignore */ });
    }, [establishments]);

    const toggleAll = () => {
        if (selectedIds.size === establishments.length) setSelectedIds(new Set());
        else setSelectedIds(new Set(establishments.map((e) => e._id)));
    };

    const toggleOne = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const copyToClipboard = async (e: React.MouseEvent, text: string) => {
        e.stopPropagation();
        try { await navigator.clipboard.writeText(text); } catch { /* ignore */ }
    };

    if (establishments.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-center">
                <Store className="w-10 h-10 text-ink-faint mb-3" />
                <p className="text-sm text-ink-faint">Aucun établissement lié</p>
            </div>
        );
    }

    return (
        <div className="bg-surface border-t border-border">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                    <thead className="bg-surface-sunken">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left w-12">
                                <input
                                    type="checkbox"
                                    className="rounded-sm border-border-strong text-accent focus:ring-accent h-4 w-4"
                                    checked={establishments.length > 0 && selectedIds.size === establishments.length}
                                    onChange={toggleAll}
                                />
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint">Contact</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint">Coordonnées</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint">Liens</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint">Établissement</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint">Ouverture</th>
                        </tr>
                    </thead>
                    <tbody className="bg-surface divide-y divide-border">
                        {establishments.map((est) => {
                            const isSelected = selectedIds.has(est._id);
                            const { id: personId, name: personName, phone, email, linkedin } = getPersonInfo(est);
                            const countLabel = personId ? managerCountLabel(managerCounts[personId]) : null;
                            // The gérant's other brands: this fiche's own is already the page's subject.
                            const ownFranchise = franchiseIdOf(est.franchise);
                            const otherBrands = personId
                                ? (managerCounts[personId]?.franchises ?? [])
                                    .filter((id) => id !== ownFranchise && brands[id])
                                    .map((id) => brands[id])
                                : [];
                            const googleRating = est.ratings?.source_summary?.[0];
                            const tags = [...(est.categories || []), ...(est.subCategories || [])];

                            return (
                                <tr
                                    key={est._id}
                                    onClick={() => navigate(`/establishments/${est._id}`)}
                                    className={`transition-colors duration-150 group cursor-pointer hover:bg-surface-sunken/60 ${isSelected ? "bg-surface-sunken/40" : ""}`}
                                >
                                    {/* Checkbox */}
                                    <td className="px-6 py-4 whitespace-nowrap align-middle" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            className="rounded-sm border-border-strong text-accent focus:ring-accent h-4 w-4"
                                            checked={isSelected}
                                            onChange={() => toggleOne(est._id)}
                                        />
                                    </td>

                                    {/* Contact */}
                                    <td className="px-6 py-4 align-middle">
                                        <div className="flex flex-col">
                                            {personName ? (
                                                <>
                                                    <span className="flex items-center gap-2">
                                                        <span className="text-sm font-medium text-ink">
                                                            {formatName(personName)}
                                                        </span>
                                                        {otherBrands.length > 0 && (
                                                            <span title={`Aussi franchisé : ${otherBrands.map((b) => b.name).join(", ")}`}>
                                                                <BrandStack brands={otherBrands} size={18} />
                                                            </span>
                                                        )}
                                                    </span>
                                                    <span className="text-xs text-ink-faint mt-0.5 italic">
                                                        Gérant
                                                    </span>
                                                    {/* The gérant's own list (same page the signal and prospection tables open),
                                                        not the row's establishment: stopPropagation keeps the row click out. */}
                                                    {countLabel && (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/signals/founder?founderName=${encodeURIComponent(personName)}`);
                                                            }}
                                                            title={`Voir les établissements de ${formatName(personName)}`}
                                                            className="text-xs text-ink-muted mt-0.5 text-left underline decoration-dotted underline-offset-2 hover:text-accent transition-colors duration-150"
                                                        >
                                                            {countLabel}
                                                        </button>
                                                    )}
                                                </>
                                            ) : (
                                                <span className="text-sm text-ink-faint italic">Enrichissement en cours...</span>
                                            )}
                                        </div>
                                    </td>

                                    {/* Coordinates */}
                                    <td className="px-6 py-4 align-middle whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center gap-1">
                                            {/* Phone */}
                                            <div className="relative">
                                                <button
                                                    className={`flex items-center justify-center w-9 h-9 rounded-lg transition-colors duration-150 ${
                                                        phone
                                                            ? "text-ink-muted hover:text-ink hover:bg-surface-sunken"
                                                            : "opacity-20 cursor-default"
                                                    }`}
                                                    disabled={!phone}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setHoveredPhone(hoveredPhone === est._id ? null : est._id);
                                                        setHoveredEmail(null);
                                                        setHoveredLinkedin(null);
                                                    }}
                                                >
                                                    <Phone className="w-5 h-5" />
                                                </button>
                                                {phone && (
                                                    <>
                                                        <span className="absolute -top-1 -right-1 bg-border text-ink text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center">
                                                            1
                                                        </span>
                                                        {hoveredPhone === est._id && (
                                                            <div
                                                                ref={phoneRef}
                                                                className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 w-64"
                                                            >
                                                                <div className="bg-surface border border-border rounded-2xl shadow-float p-4">
                                                                    <p className="text-sm font-medium text-ink mb-3">
                                                                        Numéro de téléphone
                                                                    </p>
                                                                    <div className="flex items-center justify-between gap-2">
                                                                        <span className="text-sm text-ink">{phone}</span>
                                                                        <button
                                                                            onClick={(e) => copyToClipboard(e, phone)}
                                                                            className="px-2 py-1 text-xs bg-surface-sunken hover:bg-border/50 rounded-lg transition-colors duration-150 flex-shrink-0"
                                                                        >
                                                                            Copier
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>

                                            {/* Email */}
                                            <div className="relative">
                                                <button
                                                    className={`flex items-center justify-center w-9 h-9 rounded-lg transition-colors duration-150 ${
                                                        email
                                                            ? "text-ink-muted hover:text-ink hover:bg-surface-sunken"
                                                            : "opacity-20 cursor-default"
                                                    }`}
                                                    disabled={!email}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setHoveredEmail(hoveredEmail === est._id ? null : est._id);
                                                        setHoveredPhone(null);
                                                        setHoveredLinkedin(null);
                                                    }}
                                                >
                                                    <Mail className="w-5 h-5" />
                                                </button>
                                                {email && (
                                                    <>
                                                        <span className="absolute -top-1 -right-1 bg-border text-ink text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center">
                                                            1
                                                        </span>
                                                        {hoveredEmail === est._id && (
                                                            <div
                                                                ref={emailRef}
                                                                className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 w-64"
                                                            >
                                                                <div className="bg-surface border border-border rounded-2xl shadow-float p-4">
                                                                    <p className="text-sm font-medium text-ink mb-3">
                                                                        Email disponible
                                                                    </p>
                                                                    <div className="flex items-center justify-between gap-2">
                                                                        <span className="text-sm text-ink truncate">{email}</span>
                                                                        <button
                                                                            onClick={(e) => copyToClipboard(e, email)}
                                                                            className="px-2 py-1 text-xs bg-surface-sunken hover:bg-border/50 rounded-lg transition-colors duration-150 flex-shrink-0"
                                                                        >
                                                                            Copier
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </td>

                                    {/* Links */}
                                    <td className="px-6 py-4 align-middle" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center gap-3">
                                            {/* LinkedIn */}
                                            <div className="relative flex items-center">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setHoveredLinkedin(hoveredLinkedin === est._id ? null : est._id);
                                                        setHoveredPhone(null);
                                                        setHoveredEmail(null);
                                                    }}
                                                    className={`transition-colors duration-150 ${
                                                        linkedin ? "text-ink hover:text-ink" : "text-ink-faint hover:text-ink-muted"
                                                    }`}
                                                >
                                                    <Linkedin className="w-5 h-5" />
                                                </button>
                                                {hoveredLinkedin === est._id && (
                                                    <div
                                                        ref={linkedinRef}
                                                        className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 w-64"
                                                    >
                                                        <div className="bg-surface border border-border rounded-2xl shadow-float p-4">
                                                            {linkedin ? (
                                                                <>
                                                                    <p className="text-sm text-ink-muted mb-3">
                                                                        Profil LinkedIn trouvé pour {personName}
                                                                    </p>
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            window.open(linkedin, "_blank");
                                                                        }}
                                                                        className="w-full bg-[#0077b5] text-white px-3 py-2 rounded-lg hover:bg-[#006399] transition-colors duration-150 text-sm font-medium"
                                                                    >
                                                                        Ouvrir le profil LinkedIn
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <p className="text-sm text-ink-muted mb-3">
                                                                        Aucun profil LinkedIn trouvé pour {personName || est.name}.
                                                                    </p>
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            window.open(
                                                                                `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(personName || est.name)}`,
                                                                                "_blank"
                                                                            );
                                                                        }}
                                                                        className="w-full bg-accent text-white px-3 py-2 rounded-lg hover:bg-accent-hover transition-colors duration-150 text-sm font-medium"
                                                                    >
                                                                        Rechercher manuellement
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>

                                    {/* Establishment info */}
                                    <td className="px-6 py-4 align-middle">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-surface border border-border rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                                                <EstablishmentLogo name={est.name} franchise={est.franchise} emojiClassName="text-xl" />
                                            </div>
                                            <div className="flex flex-col min-w-0">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/establishments/${est._id}`);
                                                    }}
                                                    className="text-sm font-semibold text-ink hover:text-ink hover:underline text-left truncate max-w-[200px]"
                                                >
                                                    {est.name}
                                                </button>
                                                {est.address?.city && (
                                                    <span className="text-xs text-ink-muted">
                                                        {formatCity(est.address.city)}
                                                        {est.address.postcode ? ` · ${est.address.postcode}` : ""}
                                                    </span>
                                                )}
                                                {googleRating && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (googleRating.url) window.open(googleRating.url, "_blank");
                                                        }}
                                                        className="flex items-center gap-1 text-xs text-ink-muted hover:text-ink transition-colors duration-150 hover:underline mt-0.5"
                                                        disabled={!googleRating.url}
                                                    >
                                                        <Star className="w-3.5 h-3.5 text-warning fill-current" />
                                                        <span className="font-medium text-ink">
                                                            {googleRating.rating_avg?.toFixed(1) ?? "—"}
                                                        </span>
                                                        <span className="text-ink-muted">
                                                            ({googleRating.rating_count ?? 0} avis)
                                                        </span>
                                                    </button>
                                                )}
                                                {tags.length > 0 && (
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                        {tags.slice(0, 2).map((t) => (
                                                            <span
                                                                key={t}
                                                                className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] bg-surface-sunken text-ink-muted"
                                                            >
                                                                {t}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>

                                    {/* Opening date */}
                                    <td className="px-6 py-4 align-middle whitespace-nowrap">
                                        <div className="flex items-center gap-1.5 text-sm text-ink-muted">
                                            <Calendar className="w-4 h-4 text-ink-faint flex-shrink-0" />
                                            {formatDate(est.operations?.opening_date)}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* ─────────────────────────── News list ─────────────────────────── */

function NewsList({ news }: { news: FranchiseNewsItem[] }) {
    if (news.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-center">
                <Newspaper className="w-10 h-10 text-border mb-3" />
                <p className="text-sm text-ink-faint">Aucun article lié</p>
            </div>
        );
    }
    return (
        <div className="divide-y divide-gray-100">
            {news.map((item) => {
                return (
                    <a
                        key={item._id}
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex gap-4 px-6 py-4 hover:bg-surface-sunken/60 transition-colors group"
                    >
                        {/* Thumbnail */}
                        <div className="flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden bg-surface-sunken">
                            {item.image ? (
                                <img
                                    src={item.image}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                                />
                            ) : item.source.favicon ? (
                                <div className="w-full h-full flex items-center justify-center">
                                    <img
                                        src={item.source.favicon}
                                        alt=""
                                        className="w-6 h-6"
                                        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                                    />
                                </div>
                            ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                    <Newspaper className="w-5 h-5 text-ink-faint" />
                                </div>
                            )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                                <NewsCategoryTag category={item.category} />
                                {item.city && (
                                    <span className="text-xs text-ink-muted flex items-center gap-1">
                                        <MapPin className="w-3 h-3 text-ink-faint" />
                                        {item.city}
                                    </span>
                                )}
                                <span className="text-xs text-ink-faint ml-auto flex-shrink-0">
                                    {timeAgo(item.published)}
                                </span>
                            </div>
                            <p className="text-sm font-medium text-ink leading-snug line-clamp-2 group-hover:text-black">
                                {item.title}
                            </p>
                            <p className="text-[11px] text-ink-faint mt-1 truncate flex items-center gap-1">
                                <ExternalLink className="w-3 h-3" />
                                {item.source.name}
                            </p>
                        </div>
                    </a>
                );
            })}
        </div>
    );
}

/* ─────────────────────────── Hygiene tab ─────────────────────────── */

const HYGIENE_RESULTS: { key: FranchiseHygieneResult; label: string; short: string; badge: string; dot: string }[] = [
    { key: "urgent",    label: "A corriger de manière urgente", short: "À corriger d'urgence", badge: "bg-danger-subtle text-danger",          dot: "bg-danger" },
    { key: "improve",   label: "A améliorer",                   short: "À améliorer",          badge: "bg-warning-subtle text-warning",        dot: "bg-warning" },
    { key: "ok",        label: "Satisfaisant",                  short: "Satisfaisant",         badge: "bg-surface-sunken text-ink-muted",      dot: "bg-ink-faint" },
    { key: "very_good", label: "Très satisfaisant",             short: "Très satisfaisant",    badge: "bg-success-subtle text-success",        dot: "bg-success" },
];

function hygieneResult(label?: string) {
    return HYGIENE_RESULTS.find((r) => r.label === label) ?? null;
}

/** A control this recent is news for the rep: flagged « Nouveau » in the list. */
const RECENT_CONTROL_DAYS = 30;

function daysSince(dateStr: string): number {
    return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86_400_000);
}

function controlAge(dateStr: string): string {
    const d = daysSince(dateStr);
    if (d <= 0) return "Aujourd'hui";
    if (d === 1) return "Hier";
    if (d < 14) return `Il y a ${d} jours`;
    if (d < 60) return `Il y a ${Math.floor(d / 7)} semaines`;
    if (d < 365) return `Il y a ${Math.floor(d / 30)} mois`;
    const y = Math.floor(d / 365);
    return `Il y a ${y} an${y > 1 ? "s" : ""}`;
}

const HYGIENE_SORTS: { key: FranchiseHygieneSort; label: string }[] = [
    { key: "recent", label: "Plus récents" },
    { key: "worst", label: "À corriger d'abord" },
];

/** Result chips (one at a time, counts over the current owner filter) and the order of the list. */
function HygieneToolbar({ result, sort, counts, onResult, onSort }: {
    result: FranchiseHygieneResult | null;
    sort: FranchiseHygieneSort;
    counts?: Record<FranchiseHygieneResult, number>;
    onResult: (next: FranchiseHygieneResult | null) => void;
    onSort: (next: FranchiseHygieneSort) => void;
}) {
    return (
        <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
                {HYGIENE_RESULTS.map((r) => {
                    const active = result === r.key;
                    const count = counts?.[r.key];
                    return (
                        <button
                            key={r.key}
                            type="button"
                            aria-pressed={active}
                            onClick={() => onResult(active ? null : r.key)}
                            disabled={!active && count === 0}
                            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-40 ${
                                active
                                    ? "border-ink bg-ink text-ink-inverse"
                                    : "border-border bg-surface text-ink-muted hover:border-border-strong hover:text-ink"
                            }`}
                        >
                            <span className={`h-2 w-2 rounded-full ${r.dot}`} />
                            {r.short}
                            {count !== undefined && (
                                <span className={`font-mono text-xs ${active ? "text-ink-inverse/70" : "text-ink-faint"}`}>
                                    {count.toLocaleString("fr-FR")}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
            <div role="radiogroup" aria-label="Trier" className="inline-flex rounded-lg border border-border bg-surface-sunken p-0.5">
                {HYGIENE_SORTS.map((o) => {
                    const active = sort === o.key;
                    return (
                        <button
                            key={o.key}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            onClick={() => onSort(o.key)}
                            className={`rounded-md px-3 py-1 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                                active ? "bg-surface text-ink shadow-sm" : "text-ink-muted hover:text-ink"
                            }`}
                        >
                            {o.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/** Rows arrive in the server's order (latest control first, or worst first) — never re-sorted here. */
function HygieneTab({ establishments, filtered }: { establishments: FranchiseEstablishment[]; filtered: boolean }) {
    const navigate = useNavigate();

    const rows = establishments
        .map((est) => {
            const controls = (est.signals ?? [])
                .filter((s): s is FranchiseHygieneSignal => s.type === "hygiene_control")
                .sort((a, b) => new Date(b.date ?? 0).getTime() - new Date(a.date ?? 0).getTime());
            return { est, controls };
        })
        .filter(({ controls }) => controls.length > 0);

    if (rows.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-center">
                <ShieldCheck className="w-10 h-10 text-border mb-3" />
                <p className="text-sm text-ink-faint">
                    {filtered ? "Aucun contrôle ne correspond à ces filtres" : "Aucun contrôle sanitaire enregistré"}
                </p>
            </div>
        );
    }

    return (
        <div className="bg-surface">
            {/* Column headings, desktop only: on a phone each row stacks */}
            <div className="hidden md:grid grid-cols-[minmax(0,1fr)_14rem_11rem] gap-6 border-b border-border px-6 py-2.5 text-xs font-semibold uppercase tracking-wide text-ink-faint">
                <span>Établissement</span>
                <span>Dernier résultat</span>
                <span>Dernier contrôle</span>
            </div>
            <ul className="divide-y divide-gray-100">
                {rows.map(({ est, controls }) => {
                    const latest = controls[0];
                    const meta = latest.metadata;
                    const r = hygieneResult(meta?.score_label);
                    const isNew = !!latest.date && daysSince(latest.date) <= RECENT_CONTROL_DAYS;
                    const earlier = controls.slice(1);

                    return (
                        <li key={est._id}>
                            <button
                                type="button"
                                onClick={() => navigate(`/establishments/${est._id}`)}
                                className="grid w-full grid-cols-1 gap-2 px-6 py-4 text-left transition-colors hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent md:grid-cols-[minmax(0,1fr)_14rem_11rem] md:items-center md:gap-6 group"
                            >
                                {/* Establishment */}
                                <div className="flex min-w-0 items-start gap-3">
                                    <span className={`mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full ${r?.dot ?? "bg-border"}`} />
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-medium text-ink group-hover:underline">{est.name}</p>
                                        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-faint">
                                            {est.address?.city && (
                                                <span className="flex items-center gap-1">
                                                    <MapPin className="w-3 h-3" />
                                                    {formatCity(est.address.city)}
                                                    {est.address.postcode ? ` · ${est.address.postcode}` : ""}
                                                </span>
                                            )}
                                            {earlier.length > 0 && (
                                                <span className="flex items-center gap-1.5">
                                                    {earlier.slice(0, 5).map((h, i) => {
                                                        const hr = hygieneResult(h.metadata?.score_label);
                                                        return (
                                                            <span
                                                                key={i}
                                                                title={`${hr?.short ?? h.metadata?.score_label ?? "Résultat inconnu"} · ${formatDate(h.date)}`}
                                                                className={`h-2 w-2 rounded-full ${hr?.dot ?? "bg-border"}`}
                                                            />
                                                        );
                                                    })}
                                                    {earlier.length} contrôle{earlier.length > 1 ? "s" : ""} antérieur{earlier.length > 1 ? "s" : ""}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Latest result + follow-up */}
                                <div className="flex flex-wrap items-center gap-1.5 pl-5 md:pl-0">
                                    <span className={`rounded-md px-2 py-0.5 text-xs font-medium ${r?.badge ?? "bg-surface-sunken text-ink-muted"}`}>
                                        {r?.short ?? meta?.score_label ?? "Résultat inconnu"}
                                    </span>
                                    {meta?.follow_up_type && (
                                        <span className="rounded-md bg-warning-subtle px-2 py-0.5 text-xs font-medium text-warning">
                                            {meta.follow_up_type}
                                        </span>
                                    )}
                                </div>

                                {/* Latest control date */}
                                <div className="pl-5 md:pl-0">
                                    {latest.date ? (
                                        <>
                                            <p className="flex items-center gap-2 text-sm text-ink">
                                                {controlAge(latest.date)}
                                                {isNew && (
                                                    <span className="rounded-md bg-ink px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink-inverse">
                                                        Nouveau
                                                    </span>
                                                )}
                                            </p>
                                            <p className="text-xs text-ink-faint">{formatDate(latest.date)}</p>
                                        </>
                                    ) : (
                                        <p className="text-sm text-ink-faint">Date inconnue</p>
                                    )}
                                </div>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

/* ─────────────────────────── Main page ─────────────────────────── */

// Page-level tabs, kept in the URL (?tab=) so each view can be linked to and the back button
// walks between them. The overview is the default: the network figures and its establishments.
type Tab = "overview" | "news" | "hygiene";
const TABS: Tab[] = ["overview", "news", "hygiene"];

const PAGE_SIZE = 25;

function Pager({ page, totalPages, total, onChange }: { page: number; totalPages: number; total: number; onChange: (p: number) => void }) {
    if (totalPages <= 1) return null;
    const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
        .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
        .reduce<(number | "…")[]>((acc, p, idx, arr) => {
            if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("…");
            acc.push(p);
            return acc;
        }, []);
    const from = (page - 1) * PAGE_SIZE + 1;
    const to = Math.min(total, page * PAGE_SIZE);
    return (
        <div className="flex flex-col items-center justify-between gap-3 border-t border-border px-6 py-4 sm:flex-row">
            <p className="text-sm text-ink-muted">
                <span className="font-medium text-ink">{from}–{to}</span> sur {total.toLocaleString("fr-FR")}
            </p>
            <div className="flex items-center gap-2">
                <button
                    onClick={() => onChange(page - 1)}
                    disabled={page === 1}
                    aria-label="Page précédente"
                    className="rounded-lg border border-border bg-surface p-2 text-ink-muted transition-colors duration-150 hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-40"
                >
                    <ChevronLeft className="h-4 w-4" />
                </button>
                {pages.map((p, idx) =>
                    p === "…" ? (
                        <span key={`gap-${idx}`} className="px-1 text-sm text-ink-faint">…</span>
                    ) : (
                        <button
                            key={p}
                            onClick={() => onChange(p)}
                            className={`h-9 w-9 rounded-lg text-sm font-medium transition-colors duration-150 ${
                                page === p ? "bg-ink text-ink-inverse" : "border border-border bg-surface text-ink-muted hover:bg-surface-sunken"
                            }`}
                        >
                            {p}
                        </button>
                    )
                )}
                <button
                    onClick={() => onChange(page + 1)}
                    disabled={page === totalPages}
                    aria-label="Page suivante"
                    className="rounded-lg border border-border bg-surface p-2 text-ink-muted transition-colors duration-150 hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-40"
                >
                    <ChevronRight className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}

/* ─────────────────────────── Owner filter ─────────────────────────── */

const OWNER_FILTERS: { key: FranchiseOwnerFilter; label: string; hint: string; icon: typeof Building2 }[] = [
    { key: "multi_site", label: "Multi-établissements", hint: "Gérants qui exploitent au moins 2 établissements ouverts, toutes enseignes confondues", icon: Building2 },
    { key: "multi_brand", label: "Multi-enseignes", hint: "Gérants qui exploitent au moins 2 franchises différentes", icon: Layers },
];

/** Two exclusive toggles: clicking the active one clears it. Counts are the sites each would keep. */
function OwnerFilterBar({ value, counts, onChange }: {
    value: FranchiseOwnerFilter | null;
    counts?: Record<FranchiseOwnerFilter, number>;
    onChange: (next: FranchiseOwnerFilter | null) => void;
}) {
    return (
        <div className="flex flex-wrap items-center gap-2">
            {OWNER_FILTERS.map(({ key, label, hint, icon: Icon }) => {
                const active = value === key;
                return (
                    <button
                        key={key}
                        type="button"
                        title={hint}
                        aria-pressed={active}
                        onClick={() => onChange(active ? null : key)}
                        className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                            active
                                ? "border-ink bg-ink text-ink-inverse"
                                : "border-border bg-surface text-ink-muted hover:border-border-strong hover:text-ink"
                        }`}
                    >
                        <Icon className="h-4 w-4" />
                        {label}
                        {counts && (
                            <span className={`font-mono text-xs ${active ? "text-ink-inverse/70" : "text-ink-faint"}`}>
                                {counts[key].toLocaleString("fr-FR")}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

export default function FranchiseurDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState<FranchiseDetail | null>(null);
    const [loading, setLoading] = useState(true);

    const tabParam = searchParams.get("tab") as Tab | null;
    const activeTab: Tab = tabParam && TABS.includes(tabParam) ? tabParam : "overview";
    const setActiveTab = (tab: Tab) => {
        const next = new URLSearchParams(searchParams);
        if (tab === "overview") next.delete("tab"); else next.set("tab", tab);
        setSearchParams(next);
    };

    // One page per view, so leaving a tab and coming back lands where you were.
    const [pages, setPages] = useState<Record<FranchiseEstablishmentView, number>>({ all: 1, hygiene: 1 });
    const [rows, setRows] = useState<FranchiseEstablishmentPage | null>(null);
    const [rowsLoading, setRowsLoading] = useState(false);
    const view: FranchiseEstablishmentView | null = activeTab === "overview" ? "all" : activeTab === "hygiene" ? "hygiene" : null;
    const page = view ? pages[view] : 1;

    // Kept in the URL like the tab, and shared by both tables.
    const ownersParam = searchParams.get("owners");
    const owners: FranchiseOwnerFilter | null = ownersParam === "multi_site" || ownersParam === "multi_brand" ? ownersParam : null;
    const setOwners = (next: FranchiseOwnerFilter | null) => {
        const params = new URLSearchParams(searchParams);
        if (next) params.set("owners", next); else params.delete("owners");
        setSearchParams(params);
        // A filtered list is a different list: its page 3 has nothing to do with the old page 3.
        setPages({ all: 1, hygiene: 1 });
    };

    // The hygiene table's order and result filter, in the URL too. Default: latest control first.
    const sort: FranchiseHygieneSort = searchParams.get("sort") === "worst" ? "worst" : "recent";
    const resultParam = searchParams.get("result") as FranchiseHygieneResult | null;
    const result: FranchiseHygieneResult | null = resultParam && HYGIENE_RESULTS.some((r) => r.key === resultParam) ? resultParam : null;
    const setHygieneParam = (key: "sort" | "result", value: string | null) => {
        const params = new URLSearchParams(searchParams);
        if (value) params.set(key, value); else params.delete(key);
        setSearchParams(params);
        setPages((prev) => ({ ...prev, hygiene: 1 }));
    };

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        getFranchise(id).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
        setPages({ all: 1, hygiene: 1 });
    }, [id]);

    useEffect(() => {
        if (!id || !view) return;
        let cancelled = false;
        setRowsLoading(true);
        const hygiene = view === "hygiene" ? { sort, result: result ?? undefined } : {};
        getFranchiseEstablishments(id, { view, owners: owners ?? undefined, ...hygiene, page, limit: PAGE_SIZE })
            .then((res) => {
                if (cancelled) return;
                setRows(res);
                // The server clamps an out-of-range page; follow it so the pager stays honest.
                if (res.page !== page) setPages((prev) => ({ ...prev, [view]: res.page }));
            })
            .catch(() => { if (!cancelled) setRows(null); })
            .finally(() => { if (!cancelled) setRowsLoading(false); });
        return () => { cancelled = true; };
    }, [id, view, page, owners, sort, result]);

    const changePage = (p: number) => {
        if (!view) return;
        setPages((prev) => ({ ...prev, [view]: p }));
    };

    /* ── Loading skeleton ── */
    if (loading) {
        return (
            <div className="min-h-screen bg-canvas">
                <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 motion-safe:animate-pulse md:px-8">
                    <div className="h-4 w-32 rounded-full bg-surface-sunken" />
                    <div className="flex gap-5">
                        <div className="flex-shrink-0 rounded-2xl bg-surface-sunken" style={{ width: 64, height: 64 }} />
                        <div className="flex-1 space-y-3 pt-1">
                            <div className="h-6 w-56 rounded-full bg-surface-sunken" />
                            <div className="h-3 w-80 rounded-full bg-surface-sunken" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <div key={i} className="h-28 rounded-2xl bg-surface-sunken" />
                        ))}
                    </div>
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                        <div className="h-96 rounded-2xl bg-surface-sunken lg:col-span-2" />
                        <div className="h-96 rounded-2xl bg-surface-sunken" />
                    </div>
                </div>
            </div>
        );
    }

    /* ── Not found ── */
    if (!data) {
        return (
            <div className="min-h-screen bg-surface flex flex-col items-center justify-center gap-4">
                <Store className="w-10 h-10 text-border" />
                <p className="text-sm text-ink-faint">Franchise introuvable</p>
                <button
                    onClick={() => navigate("/franchiseurs")}
                    className="px-4 py-2 bg-ink text-white text-xs rounded-lg hover:bg-ink/90 transition-colors"
                >
                    Retour aux franchises
                </button>
            </div>
        );
    }

    const { franchise, news, counts, intel } = data;
    const associated = intel.associated ?? [];

    const tabs: { key: Tab; label: string; count?: number }[] = [
        { key: "overview", label: "Vue d'ensemble" },
        { key: "news", label: "Actualités", count: news.length },
        { key: "hygiene", label: "Contrôle sanitaire", count: counts.hygiene },
    ];

    const tableBody = (render: (items: FranchiseEstablishment[]) => JSX.Element) =>
        rowsLoading && !rows ? (
            <div className="space-y-3 p-6 motion-safe:animate-pulse">
                {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-14 rounded-lg bg-surface-sunken" />)}
            </div>
        ) : (
            <div className={`transition-opacity duration-150 ${rowsLoading ? "opacity-60" : ""}`}>
                {render(rows?.items ?? [])}
                {rows && <Pager page={rows.page} totalPages={rows.totalPages} total={rows.total} onChange={changePage} />}
            </div>
        );

    return (
        <div className="min-h-screen bg-canvas">
            <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 md:px-8">

                <button
                    onClick={() => navigate("/franchiseurs")}
                    className="inline-flex items-center gap-1.5 text-sm text-ink-muted transition-colors duration-150 hover:text-ink"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Toutes les franchises
                </button>

                {/* ── Header: identity, shared by every tab ── */}
                <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                    <div className="flex min-w-0 items-start gap-5">
                        <FranchiseLogo f={franchise} size={64} />
                        <div className="min-w-0">
                            <h1 className="pt-1 font-sans text-2xl font-semibold tracking-tight text-ink">
                                {franchise.name}
                            </h1>
                            {franchise.website && (
                                <a
                                    href={franchise.website.startsWith("http") ? franchise.website : `https://${franchise.website}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-1 inline-flex items-center gap-1 text-sm text-ink-muted transition-colors duration-150 hover:text-ink hover:underline"
                                >
                                    <Globe className="h-3.5 w-3.5" />
                                    {franchise.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                                </a>
                            )}
                            {franchise.description && (
                                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink-muted">
                                    {franchise.description}
                                </p>
                            )}
                        </div>
                    </div>

                    {associated.length > 0 && (
                        <div className="flex-shrink-0 md:text-right">
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">
                                Leurs franchisés opèrent aussi
                            </p>
                            <div className="md:flex md:justify-end">
                                <BrandStack brands={associated} size={28} max={5} />
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Page tabs ── */}
                <div role="tablist" className="flex gap-1 border-b border-border">
                    {tabs.map((tab) => {
                        const isActive = activeTab === tab.key;
                        return (
                            <button
                                key={tab.key}
                                role="tab"
                                aria-selected={isActive}
                                onClick={() => setActiveTab(tab.key)}
                                className={`-mb-px flex items-center gap-2 border-b-2 px-3 py-3 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                                    isActive ? "border-accent text-ink" : "border-transparent text-ink-muted hover:text-ink"
                                }`}
                            >
                                {tab.label}
                                {tab.count !== undefined && (
                                    <span className={`rounded-full px-1.5 py-0.5 text-xs ${isActive ? "bg-ink text-ink-inverse" : "bg-surface-sunken text-ink-muted"}`}>
                                        {tab.count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {activeTab === "overview" && (
                    <>
                        <IntelKpis intel={intel} />

                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                            <IntelPanel eyebrow="Dynamique" title="Ouvertures par année">
                                <OpeningsChart intel={intel} />
                            </IntelPanel>
                            <IntelPanel eyebrow="Recoupements" title="Franchises associées">
                                <AssociatedBrands associated={associated} franchisees={intel.franchisees} />
                            </IntelPanel>
                            <IntelPanel eyebrow="Concentration" title="Départements clés">
                                <DepartmentBars intel={intel} limit={6} />
                            </IntelPanel>
                        </div>

                        <section className="overflow-hidden rounded-2xl border border-border bg-surface">
                            <header className="flex flex-wrap items-end justify-between gap-4 px-6 pt-5 pb-4">
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Réseau</p>
                                    <h2 className="mt-1 font-display text-xl font-normal leading-snug text-ink">Établissements</h2>
                                </div>
                                <div className="flex flex-wrap items-center gap-4">
                                    <OwnerFilterBar value={owners} counts={rows?.ownerCounts} onChange={setOwners} />
                                    <span className="text-sm text-ink-muted">
                                        {owners && rows ? (
                                            <><span className="font-medium text-ink">{rows.total.toLocaleString("fr-FR")}</span> sur {counts.establishments.toLocaleString("fr-FR")}</>
                                        ) : (
                                            <><span className="font-medium text-ink">{counts.establishments.toLocaleString("fr-FR")}</span> au total</>
                                        )}
                                    </span>
                                </div>
                            </header>
                            {tableBody((items) => <EstablishmentsTable establishments={items} />)}
                        </section>
                    </>
                )}

                {activeTab === "news" && (
                    <section className="overflow-hidden rounded-2xl border border-border bg-surface">
                        <NewsList news={news} />
                    </section>
                )}

                {activeTab === "hygiene" && (
                    <section className="overflow-hidden rounded-2xl border border-border bg-surface">
                        <div className="space-y-3 border-b border-border px-6 py-4">
                            <HygieneToolbar
                                result={result}
                                sort={sort}
                                counts={rows?.resultCounts}
                                onResult={(next) => setHygieneParam("result", next)}
                                onSort={(next) => setHygieneParam("sort", next === "recent" ? null : next)}
                            />
                            <OwnerFilterBar value={owners} counts={rows?.ownerCounts} onChange={setOwners} />
                        </div>
                        {tableBody((items) => <HygieneTab establishments={items} filtered={!!(result || owners)} />)}
                    </section>
                )}

                {franchise.imageUrl && activeTab === "overview" && (
                    <div className="h-44 overflow-hidden rounded-2xl bg-surface-sunken">
                        <img
                            src={franchise.imageUrl}
                            alt=""
                            className="h-full w-full object-cover"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
